import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "react-hot-toast";
import { FaTag, FaChevronLeft, FaChevronRight, FaTimes, FaWindows, FaApple, FaPlaystation, FaXbox, FaArrowRight, FaHeart, FaUser } from "react-icons/fa";
import { DiAndroid } from "react-icons/di";
import { SiNintendoswitch, SiOculus, SiSteamdeck, SiPlaystation } from "react-icons/si";
import { FiSearch, FiZap, FiGrid, FiPlay, FiShoppingCart, FiAward, FiUsers, FiStar } from "react-icons/fi";
import { BsGrid3X3Gap, BsListUl } from "react-icons/bs";
import axiosInstance from "../Utils/axiosInstance";
import { getAllActiveGamesWithPagination } from "../Redux/Slice/game.slice";
import { addToCart as addToCartAction, fetchCart } from "../Redux/Slice/cart.slice";
import { addToWishlist, removeFromWishlist, fetchWishlist } from "../Redux/Slice/wishlist.slice";
import SEOHead from "../component/SEOHead";
import BackgroundColor from "../component/BackgroundColor";
import ProductSkeleton from "../component/skeleton-loaders/ProductSkeleton";
import { TbDeviceVisionProFilled } from "react-icons/tb";
import HyperButton from "../component/HyperButton";

export default function DiscountGamesPage() {
  const { discountId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();


  const [discount, setDiscount] = useState(null);
  const [loadingDiscount, setLoadingDiscount] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [sortBy, setSortBy] = useState("popular");
  const [grid, setGrid] = useState(3);
  const [currentPage, setCurrentPage] = useState(1);
  const [isPageChanging, setIsPageChanging] = useState(false);
  const [selectedPlatforms, setSelectedPlatforms] = useState({});
  const [maxPageButtons, setMaxPageButtons] = useState(5);
  const gamesPerPage = 12;
  const [discountsList, setDiscountsList] = useState([]);
  const [isDiscountDropdownOpen, setIsDiscountDropdownOpen] = useState(false);
  const discountDropdownRef = useRef(null);

  const { paginatedGames, totalGames, totalPages, loadingStates } = useSelector((s) => s.game);
  const cartItems = useSelector((s) => s.cart?.items || []);
  const { wishlistStatus } = useSelector((s) => s.wishlist);
  const userId = localStorage.getItem("yoyouserId");

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDiscountDropdownOpen && discountDropdownRef.current && !discountDropdownRef.current.contains(event.target)) {
        setIsDiscountDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDiscountDropdownOpen]);

  useEffect(() => {
    // Fetch discount info
    const fetchDiscountInfo = async () => {
      try {
        const res = await axiosInstance.get("/discounts");
        if (res.data?.success) {
          setDiscountsList(res.data.data);
          const found = res.data.data.find(d => d._id === discountId);
          if (found) {
            setDiscount(found);
          }
        }
      } catch (err) {
        console.error("Error fetching discount details", err);
      } finally {
        setLoadingDiscount(false);
      }
    };
    fetchDiscountInfo();
  }, [discountId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [discount]);

  useEffect(() => {
    const handler = setTimeout(() => {
      dispatch(
        getAllActiveGamesWithPagination({
          page: currentPage,
          limit: gamesPerPage,
          search: searchTerm?.trim(),
          isActive: true,
          userside: "true",
          discountId,
        })
      );
    }, 400);
    return () => clearTimeout(handler);
  }, [currentPage, searchTerm, discountId, dispatch]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 600) {
        setMaxPageButtons(3);
      } else if (window.innerWidth < 1024) {
        setMaxPageButtons(4);
      } else {
        setMaxPageButtons(6);
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (paginatedGames) {
      setIsPageChanging(false);
    }
  }, [paginatedGames]);

  const toggleWishlist = (gameId) => {
    if (!userId) {
      navigate("/login");
      return;
    }
    const isInWishlist = wishlistStatus[gameId];
    if (isInWishlist) {
      dispatch(removeFromWishlist({ gameId }));
    } else {
      dispatch(addToWishlist({ gameId }));
    }
  };

  const handleAddToCart = (game, platform) => {
    if (!userId) {
      navigate("/login");
      return;
    }
    dispatch(addToCartAction({ gameId: game._id, platform }));
  };

  const isGameInCart = (gameId, platform) => {
    return cartItems.some(
      (item) =>
        String(item.game?._id || item.game) === String(gameId) &&
        item.platform === platform
    );
  };

  const getBestPriceInfo = (game) => {
    const platforms = game.platforms || {};
    const prices = [];
    Object.keys(platforms).forEach((p) => {
      if (platforms[p]?.available) {
        prices.push(platforms[p].discountedPrice !== undefined ? platforms[p].discountedPrice : platforms[p].price);
      }
    });
    return prices.length === 0 ? "Free" : Math.min(...prices);
  };

  const getOriginalPriceInfo = (game) => {
    const platforms = game.platforms || {};
    const prices = [];
    Object.keys(platforms).forEach((p) => {
      if (platforms[p]?.available) {
        prices.push(platforms[p].originalPrice !== undefined ? platforms[p].originalPrice : platforms[p].price);
      }
    });
    return prices.length === 0 ? null : Math.min(...prices);
  };

  const formattedDate = (dStr) => {
    if (!dStr) return "";
    return new Date(dStr).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  };

  const getFormattedTitle = (title) => {
    if (!title) return "";
    const words = title.split(" ");
    if (words.length === 1) {
      return <span className="gradient-text">{words[0]}</span>;
    }
    return (
      <>
        <span className="gradient-text">{words[0]} </span>
        {words.slice(1).join(" ")}
      </>
    );
  };

  return (
    <>
      <SEOHead
        title={discount?.title ? `${discount.title} – Discount Sale` : "Discount Sale"}
        description={
          discount?.description
            ? discount.description.substring(0, 160)
            : "Shop discounted games on Yoyo Game. Limited-time deals on top titles across PC, mobile, console and VR platforms."
        }
        keywords={[
          discount?.title,
          "discount games",
          "sale games",
          "cheap games",
          "game deals",
          "Yoyo Game sale",
        ]
          .filter(Boolean)
          .join(", ")}
        url={`https://yoyokhel.com/discount/${discountId}/games`}
        breadcrumbs={[
          { name: "Games", url: "https://yoyokhel.com/games" },
          {
            name: discount?.title || "Discount Sale",
            url: `https://yoyokhel.com/discount/${discountId}/games`,
          },
        ]}
        jsonLd={
          discount
            ? {
                "@context": "https://schema.org",
                "@type": "OfferCatalog",
                "name": discount.title || "Discount Sale",
                "description": discount.description || "Discounted games on Yoyo Game",
                "url": `https://yoyokhel.com/discount/${discountId}/games`,
              }
            : undefined
        }
      />
      <style>{`
        .disc-percent-row {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          line-height: 0.85;
        }
        .disc-percent-num {
          font-size: clamp(3.5rem, 6vw, 5.2rem);
          font-weight: 950;
          color: transparent;
          -webkit-text-stroke: 2px #5b5df2;
          filter: drop-shadow(0 0 8px rgba(91, 93, 242, 0.35));
        }
        .disc-percent-symbol-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }
        .disc-percent-symbol {
          font-size: clamp(2.2rem, 4vw, 3.2rem);
          font-weight: 950;
          color: #c026d3;
          filter: drop-shadow(0 0 8px rgba(192, 38, 211, 0.4));
        }
        .disc-hanging-tag {
          position: absolute;
          top: -2px;
          right: -10px;
          transform: rotate(50deg);
          color: #c026d3;
          font-size: 1.1rem;
          filter: drop-shadow(0 0 3px rgba(192, 38, 211, 0.4));
        }
        .disc-off-text {
          font-size: clamp(2rem, 3.5vw, 3rem);
          font-weight: 950;
          color: transparent;
          -webkit-text-stroke: 1.5px #ffffff;
          letter-spacing: 0.02em;
          filter: drop-shadow(0 0 8px rgba(255, 255, 255, 0.2));
        }
        @keyframes ribbonWave {
          0% {
            transform: rotateY(0deg) rotateX(0deg) rotateZ(0deg);
          }
          25% {
            transform: rotateY(15deg) rotateX(10deg) rotateZ(-2deg);
          }
          50% {
            transform: rotateY(0deg) rotateX(0deg) rotateZ(0deg);
          }
          75% {
            transform: rotateY(-15deg) rotateX(10deg) rotateZ(2deg);
          }
          100% {
            transform: rotateY(0deg) rotateX(0deg) rotateZ(0deg);
          }
        }
        .waving-ribbon {
          transform-origin: top center;
          animation: ribbonWave 3s ease-in-out infinite;
          transform-style: preserve-3d;
          perspective: 1000px;
        }
      `}</style>
      <BackgroundColor className="relative mx-auto w-[95%] sm:w-[92%] md:w-[90%] lg:max-w-[80%] min-h-screen pt-10 pb-16">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative overflow-hidden"
        >
          <div className="relative pt-20 pb-6 lg:py-16">
            <h1 className="text-[25px] md:text-[60px] font-bold mb-3 text-center">
              {discount ? getFormattedTitle(discount.title) : <><span className="gradient-text">Discount </span>Games</>}
            </h1>
            <p className="text-sm lg:text-base text-primary-light/60 max-w-7xl mx-auto text-center mb-5 line-clamp-3 md:line-clamp-none">
              {discount
                ? `Welcome to ${discount.title} center, You will be able to find all the games at one place. You can explore a very large and diverse collection or range of types or categories such as racing, sports, action, adventure, puzzles, and more. We have something for everyone, whether you prefer thrilling missions, a style of fighting, often in video games or sports, undisturbed games. Survey our whole collection, find new books and games, and have a relaxation and enjoyment at your fingertips with up to ${discount.discountValue}% OFF.`
                : "Explore our discounted collection of games! Find the best deals and offers here."}
            </p>
          </div>
        </motion.div>
        {/* Filters and Search controls */}
        <div className="flex justify-between items-center mb-6 gap-4 flex-wrap">
          <h2 className="text-xl md:text-2xl font-bold text-white tracking-wider flex items-center gap-2">
            Discount Games <span className="text-white/40 text-sm">({totalGames || 0})</span>
          </h2>
          <div className="flex flex-col md:flex-row items-center gap-3 w-full xl:w-auto justify-center md:justify-end md:ml-auto xl:ml-0">
            {/* Search */}
            <div className="relative w-full md:w-64">
              <input
                type="text"
                placeholder="Search games..."
                value={searchTerm}
                onChange={(e) => { setCurrentPage(1); setSearchTerm(e.target.value); }}
                className="pl-10 pr-4 py-2 bg-white/5 border border-white/15 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm w-full transition-all"
              />
              <FiSearch className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-white/40" />
            </div>
            {/* Discount Campaign Dropdown Selector */}
            <div className="relative inline-block text-left w-full md:w-64" ref={discountDropdownRef}>
              <button
                onClick={() => setIsDiscountDropdownOpen(!isDiscountDropdownOpen)}
                className="bg-white/5 border border-white/15 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 backdrop-blur flex justify-between items-center w-full text-sm hover:bg-white/10 transition"
              >
                <span className="truncate">{discount?.title || "Select Discount Campaign"}</span>
                <svg
                  className={`w-4 h-4 ml-2 transition-transform ${isDiscountDropdownOpen ? "rotate-180" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {isDiscountDropdownOpen && (
                <div className="text-sm absolute right-0 mt-2 w-full bg-black/95 backdrop-blur border border-white/10 rounded-xl shadow-2xl text-white z-50 overflow-hidden max-h-60 overflow-y-auto no-scrollbar">
                  {discountsList.map((d) => (
                    <div
                      key={d._id}
                      onClick={() => {
                        setIsDiscountDropdownOpen(false);
                        setCurrentPage(1);
                        navigate(`/discount/${d._id}`);
                      }}
                      className={`px-4 py-2.5 transition-colors cursor-pointer ${discount?._id === d._id ? "bg-purple-600/40 text-white font-semibold" : "hover:bg-white/10 text-white/90"}`}
                    >
                      {d.title} ({d.discountValue}% OFF)
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Grid Toggle */}
            <div className="hidden md:flex items-center gap-1.5 bg-white/5 border border-white/10 p-1 rounded-xl">
              <button
                className={`p-1.5 rounded-lg text-sm ${grid === 3 ? "bg-purple-600 text-white" : "text-white/60 hover:text-white"}`}
                onClick={() => setGrid(3)}
              >
                <BsGrid3X3Gap size={16} />
              </button>
              <button
                className={`p-1.5 rounded-lg text-sm ${grid === 1 ? "bg-purple-600 text-white" : "text-white/60 hover:text-white"}`}
                onClick={() => setGrid(1)}
              >
                <BsListUl size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Product Grid */}
        {loadingStates.getAllActiveGamesWithPagination || isPageChanging ? (
          <ProductSkeleton grid={grid} />
        ) : !paginatedGames || paginatedGames.length === 0 ? (
          <div className="text-center py-20 bg-white/3 border border-white/5 rounded-3xl backdrop-blur-md">
            <p className="text-white/40 text-lg font-medium italic">No games found with this discount.</p>
          </div>
        ) : (
          <div className={grid === 3 ? "grid grid-cols-1 md600:grid-cols-2 xl:grid-cols-3 3xl:grid-cols-4 gap-6" : "space-y-4"}>
            {paginatedGames.map((game) => {
              const selectedPlatform =
                selectedPlatforms[game._id] ||
                Object.keys(game.platforms || {}).filter(k => game.platforms[k]?.available)[0] ||
                "windows";

              const platformInfo = game.platforms?.[selectedPlatform] || {};
              const price = platformInfo.price || 0;
              const originalPrice = platformInfo.originalPrice || price;
              const discountedPrice = platformInfo.discountedPrice || price;
              const discountPercentage = platformInfo.discountPercentage || discount?.discountValue || game.discount?.discountPercentage || 0;
              const hasDiscount = discountPercentage > 0;
              const size = platformInfo.size || "N/A";

              return (
                <motion.div
                  key={game._id}
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                  className={`group relative overflow-hidden rounded-[24px] border border-white/5 bg-[#1a1a1e] transition-all duration-300 hover:border-purple-500/50 hover:shadow-[0_0_35px_rgba(147,51,234,0.15)] ${grid === 1 ? "flex flex-col md:flex-row items-center gap-6" : "flex flex-col justify-between h-full"
                    }`}
                >
                  {/* Image Container */}
                  <div
                    className={`relative overflow-hidden  cursor-pointer ${grid === 1 ? "w-full md:w-64 h-48 flex-shrink-0" : "h-56"}`}
                    onClick={() => navigate(`/gamedetails/${game._id}`)}
                  >
                    <img
                      src={game.cover_image?.url || game.cover_image}
                      alt={game.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />

                    {/* Premium 3D Discount Ribbon Badge */}
                    {hasDiscount && (
                      <div className="absolute top-0 left-7 z-10 drop-shadow-[0_5px_8px_rgba(0,0,0,0.5)] waving-ribbon">
                        <div
                          className="bg-gradient-to-b from-red-600 via-red-500 to-red-700 text-white px-2.5 pt-2 pb-3.5 flex flex-col items-center justify-center font-black text-center border-t-2 border-red-400/50"
                          style={{
                            clipPath: 'polygon(0 0, 100% 0, 100% 100%, 50% 85%, 0 100%)',
                            minWidth: '40px',
                            boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.4)'
                          }}
                        >
                          <span className="text-md leading-none tracking-tighter font-extrabold" style={{ textShadow: '1px 1px 1px rgba(0,0,0,0.5)' }}>
                            {discountPercentage}%
                          </span>
                          <span className="text-xs tracking-wider uppercase leading-none mt-0.5 font-bold opacity-90" style={{ textShadow: '1px 1px 1px rgba(0,0,0,0.5)' }}>
                            OFF
                          </span>
                        </div>
                      </div>
                    )}

                    {/* New Badge */}
                    {game.isNew && (
                      <div className="absolute top-4 right-4 z-10 drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)]">
                        <span className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-2.5 py-0.5 rounded text-[9px] font-black tracking-widest uppercase shadow-md animate-pulse">
                          NEW
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Info Content */}
                  <div className={`flex flex-col p-4 justify-between flex-grow ${grid === 1 ? "w-full" : "pt-4"}`}>
                    <div>
                      <h3
                        className="text-lg font-bold text-white mb-2 hover:text-purple-300 transition-colors cursor-pointer tracking-wide line-clamp-1"
                        onClick={() => navigate(`/gamedetails/${game._id}`)}
                      >
                        {game.title}
                      </h3>
                      <p className="text-gray-400 text-xs line-clamp-2 leading-relaxed mb-4 font-light">
                        {game.description}
                      </p>

                      {/* Platforms */}
                      <div className="mb-4">
                        <span className="text-xs font-bold text-gray-400 uppercase tracking-widest block mb-2">Platforms :</span>
                        <div className="flex flex-wrap gap-2">
                          {Object.keys(game.platforms).map((platform) => {
                            if (!game.platforms[platform]?.available) return null;
                            return (
                              <div
                                key={platform}
                                className={`flex items-center`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setSelectedPlatforms((prev) => ({ ...prev, [game._id]: platform }));
                                }}
                              >
                                {platform === "windows" && (
                                  <FaWindows
                                    title="Show Windows Price"
                                    className={`${selectedPlatform === platform
                                      ? "text-blue-500"
                                      : ""
                                      }`}
                                  />
                                )}
                                {platform === "ios" && (
                                  <FaApple
                                    title="Show Ios Price"
                                    className={`${selectedPlatform === platform
                                      ? "text-white"
                                      : ""
                                      }`}
                                  />
                                )}
                                {platform === "android" && (
                                  <DiAndroid
                                    title="Show Android Price"
                                    className={`${selectedPlatform === platform
                                      ? "text-green-500"
                                      : ""
                                      }`}
                                  />
                                )}
                                {platform === "nintendo" && (
                                  <SiNintendoswitch
                                    title="Show Nintendo Price"
                                    className={`${selectedPlatform === platform
                                      ? "text-red-500"
                                      : ""
                                      }`}
                                  />
                                )}
                                {platform === "quest" && (
                                  <SiOculus
                                    title="Show Quest Price"
                                    className={`${selectedPlatform === platform
                                      ? "text-sky-400"
                                      : ""
                                      }`}
                                  />
                                )}
                                {platform === "playstation" && (
                                  <SiPlaystation
                                    title="Show PlayStation Price"
                                    className={`${selectedPlatform === platform
                                      ? "text-blue-500"
                                      : ""
                                      }`}
                                  />
                                )}
                                {platform === "xbox" && (
                                  <FaXbox
                                    title="Show Xbox Price"
                                    className={`${selectedPlatform === platform
                                      ? "text-green-500"
                                      : ""
                                      }`}
                                  />
                                )}
                                {platform === "visionpro" && (
                                  <TbDeviceVisionProFilled
                                    title="Show Vision Pro Price"
                                    className={`${selectedPlatform === platform
                                      ? "text-purple-400"
                                      : ""
                                      }`}
                                  />
                                )}
                                {platform === "steamdeck" && (
                                  <SiSteamdeck
                                    title="Show Steam Deck Price"
                                    className={`${selectedPlatform === platform
                                      ? "text-blue-400"
                                      : ""
                                      }`}
                                  />
                                )}
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Footer Row */}
                    <div className="flex items-center justify-between border-t border-white/5 pt-4 gap-2">
                      <div className="flex flex-col text-xs lg:text-sm text-white">
                        <div className="flex items-center gap-1">
                          <span className="text-gray-400">Price -</span>
                          {hasDiscount ? (
                            <div className="flex items-center gap-1.5">
                              <span className="text-green-400 font-extrabold drop-shadow-[0_0_8px_rgba(74,222,128,0.25)]">
                                ${discountedPrice}
                              </span>
                              <span className="text-gray-500 line-through text-[10px] font-normal">
                                ${originalPrice}
                              </span>
                            </div>
                          ) : (
                            <span className="font-extrabold">
                              ${price === 0 || price === "Free" ? "Free" : price}
                            </span>
                          )}
                        </div>
                        {/* <span className="text-gray-400 text-[10px] mt-0.5">
                          Size - <span className="text-gray-300 font-medium">{size}</span>
                        </span> */}
                      </div>

                      {isGameInCart(game._id, selectedPlatform) ? (
                        <button
                          disabled
                          className="bg-gray-700/80 text-gray-400 px-4 py-2 text-xs rounded-xl font-bold flex items-center gap-2 cursor-not-allowed border border-white/5 justify-center"
                        >
                          <FiShoppingCart className="w-3.5 h-3.5" />
                          Added
                        </button>
                      ) : !userId ? (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            navigate("/login");
                          }}
                          className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white px-4 py-2 text-xs rounded-xl font-bold flex items-center gap-2 transition-all hover:scale-105 shadow-md shadow-purple-500/20 active:scale-95 justify-center"
                        >
                          <FaUser className="w-3.5 h-3.5" />
                          Login to Add
                        </button>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleAddToCart(game, selectedPlatform);
                          }}
                          className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white px-4 py-2 text-xs rounded-xl font-bold flex items-center gap-2 transition-all hover:scale-105 shadow-md shadow-purple-500/20 active:scale-95 justify-center"
                        >
                          <FiShoppingCart className="w-3.5 h-3.5" />
                          Add To Cart
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center md600:justify-end items-center mt-8 space-x-2 col-span-4">
            <button
              onClick={() => {
                setIsPageChanging(true);
                setCurrentPage((prev) => Math.max(1, prev - 1));
              }}
              disabled={currentPage === 1}
              className="p-1 md:p-2 rounded-md text-white hover:bg-gray-700/40 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
            >
              <FaChevronLeft />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(
              (page) => {
                const startPage = Math.max(
                  1,
                  currentPage - Math.floor(maxPageButtons / 2)
                );
                const endPage = Math.min(
                  totalPages,
                  startPage + maxPageButtons - 1
                );

                if (
                  page === 1 ||
                  page === totalPages ||
                  (page >= startPage && page <= endPage)
                ) {
                  return (
                    <button
                      key={page}
                      onClick={() => {
                        setIsPageChanging(true);
                        setCurrentPage(page);
                      }}
                      className={`px-2 py-1 md:px-4 md:py-2 rounded-md text-sm sm:text-base font-medium transition-all duration-300 ring-[1px] ring-primary-light/10
                                        ${currentPage === page
                          ? "bg-gradient-primary text-white"
                          : " text-white hover:bg-gray-700/40"
                        }`}
                    >
                      {page}
                    </button>
                  );
                }
                if (
                  (page === startPage - 1 && startPage > 2) ||
                  (page === endPage + 1 && endPage < totalPages - 1)
                ) {
                  return (
                    <span key={page} className="text-white">
                      ...
                    </span>
                  );
                }
                return null;
              }
            )}

            <button
              onClick={() => {
                setIsPageChanging(true);
                setCurrentPage((prev) => Math.min(totalPages, prev + 1));
              }}
              disabled={currentPage === totalPages}
              className="p-1 md:p-2 rounded-md  text-white hover:bg-gray-700/40 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
            >
              <FaChevronRight />
            </button>
          </div>
        )}
      </BackgroundColor>
    </>
  );
}
